import React, { useState } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Envelope, MapPin, Phone, PaperPlaneTilt } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { inquiriesAPI } from '../../lib/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await inquiriesAPI.create({
        ...formData,
        inquiry_type: 'contact'
      });
      toast.success('Message sent successfully. We will respond shortly.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-20 bg-[#1A1614] text-center" data-testid="contact-header">
        <p className="text-overline mb-4">Get in Touch</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
          Contact
        </h1>
        <p className="text-[#8A847A] max-w-2xl mx-auto px-4">
          Reach out to the Grand Priory Chancery for inquiries and correspondence
        </p>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white" data-testid="contact-content">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl text-[#1A1614] mb-8">
                Grand Priory Chancery
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-[#7B1818]/10 flex items-center justify-center text-[#7B1818]">
                    <Envelope size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1614] mb-1">Email</h3>
                    <p className="text-[#4A453E]">info@scmoth1804caribbean.org</p>
                    <p className="text-sm text-[#8A847A] mt-1">For official correspondence</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-[#7B1818]/10 flex items-center justify-center text-[#7B1818]">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1614] mb-1">Jurisdiction</h3>
                    <p className="text-[#4A453E]">Caribbean Region</p>
                    <p className="text-sm text-[#8A847A] mt-1">
                      Serving nations and territories throughout the Caribbean
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex-shrink-0 bg-[#7B1818]/10 flex items-center justify-center text-[#7B1818]">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1614] mb-1">Protocol</h3>
                    <p className="text-[#4A453E]">By Appointment Only</p>
                    <p className="text-sm text-[#8A847A] mt-1">
                      Please contact via email to schedule
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-[#F5F2EB] border border-[#D8D2C4]">
                <h3 className="font-heading text-xl text-[#1A1614] mb-3">Response Times</h3>
                <p className="text-sm text-[#4A453E] leading-relaxed">
                  The Chancery endeavors to respond to all inquiries within 5-7 business days. 
                  For urgent matters related to ongoing applications or scheduled events, 
                  please indicate as such in your correspondence.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#F5F2EB] border border-[#D8D2C4] p-8" data-testid="contact-form-container">
              <h2 className="font-heading text-2xl text-[#1A1614] mb-6">Send a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-[#1A1614] mb-2">Your Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1614] mb-2">Email Address *</label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1614] mb-2">Subject *</label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                    data-testid="contact-input-subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1614] mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                    data-testid="contact-input-message"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-8 py-3 bg-[#7B1818] text-white hover:bg-[#5C1212] rounded-none flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {submitting ? 'Sending...' : (
                    <>
                      <PaperPlaneTilt size={18} />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ContactPage;
